export const imageFileId = file => String(file?.id || file?._id || file?.fileId || '');
export const imageFileUrl = file => file?.cdnPath || file?.url || file?.uri || '';
const entityId = value => typeof value === 'object' ? String(value?.id || value?._id || '') : String(value || '');
export const imageFileKey = file => imageFileId(file)
  || (file?.taskId && file?.path ? `${entityId(file.taskId)}:${file.path}` : imageFileUrl(file));
export function isImageArtifact(file) {
  return file?.fileType === 'image'
    || String(file?.mimeType || file?.contentType || '').toLowerCase().startsWith('image/')
    || [file?.name, file?.path, imageFileUrl(file)].some(value => /\.(png|jpe?g|webp|gif|avif|bmp|svg)(?:[?#]|$)/i.test(value || ''));
}

const messageImages = message => [
  ...(Array.isArray(message?.images) ? message.images.map(image => ({ ...image, fileType: 'image' })) : []),
  ...(Array.isArray(message?.files) ? message.files : []),
  ...(Array.isArray(message?.parameters?.coworkerArtifacts)
    ? message.parameters.coworkerArtifacts.map(artifact => ({ ...artifact, ...artifact.file })) : []),
].filter(isImageArtifact);

// Settled Work outputs are attached to conversation messages, while the live
// transcript may contain only its newest events. Resolve the owning chat
// explicitly; a reused source is never permission to mix two chats' histories.
export function conversationImageOutputs(chats, file) {
  const conversations = Array.isArray(chats) ? chats : [];
  const conversationId = entityId(file?.conversationId);
  const taskId = entityId(file?.taskId);
  const sourceId = imageFileId(file);
  const sourceUrl = imageFileUrl(file);
  const candidates = conversationId
    ? conversations.filter(chat => entityId(chat.id || chat._id) === conversationId)
    : taskId
      ? conversations.filter(chat => entityId(chat.coworkerSession) === taskId)
      : conversations.filter(chat => (chat.messages || []).some(message => messageImages(message).some(image =>
        sourceId ? imageFileId(image) === sourceId : sourceUrl && imageFileUrl(image) === sourceUrl)));
  if (candidates.length !== 1) return [];
  const owner = candidates[0];
  const ownerConversationId = entityId(owner.id || owner._id);
  const ownerTaskId = entityId(owner.coworkerSession);
  if (taskId && ownerTaskId !== taskId) return [];
  const outputs = new Map();
  for (const message of owner.messages || []) {
    if (message?.flags?.isDeleted) continue;
    for (const image of messageImages(message)) {
      const candidate = { ...image, conversationId: ownerConversationId, ...(ownerTaskId ? { taskId: ownerTaskId } : {}) };
      if (!candidate.name && candidate.path) candidate.name = candidate.path.split('/').pop();
      const key = imageFileKey(candidate);
      if (key) outputs.set(key, { ...outputs.get(key), ...candidate });
    }
  }
  return [...outputs.values()];
}

// Show outputs from the owning task only; similarly named files in another
// task must never become edit targets or appear as versions of this image.
export function taskImageOutputs(eventsByTask, file) {
  const id = imageFileId(file);
  const owner = file?.taskId
    ? [entityId(file.taskId), eventsByTask?.[entityId(file.taskId)]]
    : id && Object.entries(eventsByTask || {}).find(([, items]) => Array.isArray(items) && items.some(event =>
      event.type === 'artifact_ready' && imageFileId(event.artifact?.file || event.artifact) === id));
  const [taskId, events] = owner || [];
  const outputs = new Map();
  if (file && imageFileKey(file)) outputs.set(imageFileKey(file), file);
  for (const event of [...(Array.isArray(events) ? events : [])].sort((a, b) => Number(a.seq) - Number(b.seq))) {
    const artifact = event.artifact;
    if (event.type !== 'artifact_ready' || !artifact) continue;
    const candidate = { ...artifact, ...artifact.file, taskId, ...(file?.conversationId ? { conversationId: entityId(file.conversationId) } : {}) };
    if (!candidate.name && candidate.path) candidate.name = candidate.path.split('/').pop();
    const key = imageFileKey(candidate);
    if (!key || !isImageArtifact(candidate)) continue;
    const unpersistedKey = taskId && candidate.path ? `${taskId}:${candidate.path}` : '';
    const previous = outputs.get(key) || outputs.get(unpersistedKey);
    if (imageFileId(candidate) && unpersistedKey) outputs.delete(unpersistedKey);
    outputs.set(key, { ...previous, ...candidate });
  }
  return [...outputs.values()];
}

export function imageEditPrompt({ tool, instruction = '', region, size = '1:1', source }) {
  const requests = {
    edit: 'Edit the attached source image.',
    comment: 'Edit the attached source image according to this comment.',
    background: 'Remove the background from the attached source image. Keep the subject intact and return a PNG with a transparent background.',
    remove: 'Remove the object in the selected area of the attached source image and naturally fill the area to match its surroundings.',
    resize: `Resize or recompose the attached source image to a ${size} aspect ratio. Preserve the subject and avoid stretching.`,
    variation: 'Generate a new variation of the attached source image, preserving its main subject and visual direction.',
    generate: 'Generate a new image from scratch. Do not use existing attachments as reference images for this request.',
  };
  if (!requests[tool]) throw new Error('Choose an image tool.');
  if (tool === 'remove' && !region) throw new Error('Select the object to remove on the image first.');
  if (['edit', 'comment', 'generate'].includes(tool) && !instruction.trim()) throw new Error('Describe what you would like the AI to create or change.');
  const location = region && ['edit', 'comment', 'remove'].includes(tool)
    ? `Focus on the rectangle from (${Math.round(region.x * 100)}%, ${Math.round(region.y * 100)}%) to (${Math.round((region.x + region.width) * 100)}%, ${Math.round((region.y + region.height) * 100)}%) of the source image, measured from the top-left. Preserve the rest of the image wherever possible.` : '';
  const target = tool !== 'generate' && source ? `Source image: ${source.name || 'image'} (file ID: ${imageFileId(source)}). Apply this request to that attached file.` : '';
  return [requests[tool], target, location, instruction.trim(), 'Return the finished image as an image file.'].filter(Boolean).join('\n\n');
}
